/* eslint-disable no-unused-vars */
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { BatchUpdatePayload, BatchUpdateQuery } from '../../types/BatchUpdate';
import { Voucher } from '../../types/Voucher';

type PayloadTypes = string | number | Date | object;

interface BatchContextType {
  payload: BatchUpdatePayload;
  toggleId: (voucher: Voucher) => void;
  handleUpdate: (params: { value: PayloadTypes; prop: keyof BatchUpdateQuery }) => void;
  clearSelection: () => void;
}

const BatchUpdateContext = createContext<BatchContextType | undefined>(undefined);

export const useBatchUpdate = () => {
  const context = useContext(BatchUpdateContext);
  if (!context) {
    throw new Error('useBatchUpdate must be used within a BatchUpdateProvider');
  }
  return context;
};

interface BatchProviderProps {
  children: ReactNode;
}

export const BatchUpdateProvider = ({ children }: BatchProviderProps) => {
  const [payload, setPayload] = useState<BatchUpdatePayload>({
    batches: [
      {
        ids: [],
        vouchers: [],
      },
    ],
  });

  const toggleId = (voucher: Voucher) => {
    setPayload(prev => {
      const idExists = prev.batches[0].ids.includes(voucher.id);
      const updatedIds = idExists
        ? prev.batches[0].ids.filter(c => c !== voucher.id)
        : [...prev.batches[0].ids, voucher.id];

      const updatedVouchers = idExists
        ? prev.batches[0].vouchers.filter(v => v.id !== voucher.id) // Remove voucher
        : [...prev.batches[0].vouchers, voucher]; // Add voucher

      return {
        ...prev,
        batches: [
          {
            ...prev.batches[0],
            ids: updatedIds,
            vouchers: updatedVouchers,
          },
        ],
      };
    });
  };

  const handleUpdate = ({ value, prop }: { value: PayloadTypes; prop: keyof BatchUpdateQuery }) => {
    setPayload(prev => ({
      ...prev,
      batches: prev.batches.map((batch, index) =>
        index === 0
          ? {
              ...batch,
              query: {
                ...(batch.query || {}),
                [prop]: value,
              },
            }
          : batch,
      ),
    }));
  };

  const clearSelection = () => {
    setPayload({
      batches: [
        {
          ids: [],
          vouchers: [],
        },
      ],
    });
  };

  return (
    <BatchUpdateContext.Provider value={{ payload, toggleId, handleUpdate, clearSelection }}>
      {children}
    </BatchUpdateContext.Provider>
  );
};
